// FE client for the app-session API (knowledge/10, cat 48.16) — per-actor Recent Apps +
// last-screen restore. PERSONAL state: the backend keys every call on the session actor
// (own-rows only); no scope wall involved beyond the workspace filter on recents.
import { classifyFetchError } from "../state/denialErrors";

export interface AppSessionRow {
  id: string;
  app_id: string;
  scope_node_id: string;
  scope_path: string;
  last_screen: string | null;
  last_seen_at: string;
}

const BASE = "/api/workspace/app-sessions";

/** Upsert the caller's session for (app, scope); bumps recency; optionally records the screen. */
export async function upsertAppSession(
  appId: string,
  scopeNodeId: string,
  lastScreen?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(BASE, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ app_id: appId, scope_node_id: scopeNodeId, last_screen: lastScreen }),
  });
  if (!res.ok) throw await classifyFetchError(res, "appSessions");
}

/** The caller's recents in a workspace, newest first (server-capped). */
export async function fetchRecentAppSessions(
  workspaceId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AppSessionRow[]> {
  const res = await fetchImpl(`${BASE}?workspace_id=${encodeURIComponent(workspaceId)}`, { method: "GET", credentials: "include" });
  if (!res.ok) throw await classifyFetchError(res, "appSessions");
  return (await res.json()) as AppSessionRow[];
}

/** Clear ONE recents entry (own row; idempotent server-side). */
export async function deleteAppSession(
  appId: string,
  scopeNodeId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(
    `${BASE}/one?app_id=${encodeURIComponent(appId)}&scope_node_id=${encodeURIComponent(scopeNodeId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) throw await classifyFetchError(res, "appSessions");
}

/** Clear ALL of the caller's recents in a workspace (own rows only). */
export async function clearAppSessions(
  workspaceId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(`${BASE}?workspace_id=${encodeURIComponent(workspaceId)}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) throw await classifyFetchError(res, "appSessions");
}

/** The caller's session for (app, scope) — the restore read. 404 → null (no session yet). */
export async function fetchAppSessionOne(
  appId: string,
  scopeNodeId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AppSessionRow | null> {
  const res = await fetchImpl(
    `${BASE}/one?app_id=${encodeURIComponent(appId)}&scope_node_id=${encodeURIComponent(scopeNodeId)}`,
    { method: "GET", credentials: "include" },
  );
  if (res.status === 404) return null;
  if (!res.ok) throw await classifyFetchError(res, "appSessions");
  return (await res.json()) as AppSessionRow;
}
